import {Middleware} from "redux";
import uuid from "uuid/v4";
import {getType} from "typesafe-actions";
import * as signalR from "@microsoft/signalr";
import {buildUrl, getCurrentApiEndpoint, sessionId} from "../../../shared/utils/apiCall";
import {trackError} from "../../log/actions";
import {IApplicationState} from "../..";
import {loadScene} from "../../routing/actions";
import {index} from "./handlers";
import IAnalyticsAction from "./IAnalyticsAction";
import {LogLevel} from "../../log/types";
import callOnIdle from "../../../shared/utils/callOnIdle";
import flatten from "../../../shared/utils/flatten";
import { SDK } from "../../../sdk";

let sceneId: string;

export const analyticsMiddleware: Middleware = store => next => {
    const { settings, player } = store.getState() as IApplicationState;
    const { api, general: { logActions }, allowDebug } = settings;

    const socketEndpoint = getCurrentApiEndpoint(api.endpoints);
    const socketHubUrl = buildUrl(socketEndpoint, api.socketUriTemplate, {});

    const socketConnection = new signalR.HubConnectionBuilder()
        .withUrl(socketHubUrl)
        .build();

    socketConnection.onclose(error=> {
        store.dispatch(trackError({
            logLevel: LogLevel.Warn,
            category: "AnalyticsMiddleware",
            message: "Connection error",
            error: error,
            data: {
                socketHubUrl
            }
        }));

        setTimeout(() => start(), api.socketReconnectionTimeout);
    });

    const invoke = (methodName: string, args: any[], attempt = 0) => {
        if (socketConnection.state !== signalR.HubConnectionState.Connected) {
            setTimeout(() => invoke(methodName, args, attempt), api.socketReconnectionTimeout);
            return;
        }

        socketConnection.invoke(methodName, ...args)
            .catch((error: any) => {
                if (attempt < api.retryCount) {
                    setTimeout(() => invoke(methodName, args, attempt + 1), api.retryTimeout);
                } else {
                    store.dispatch(trackError({
                        category: "AnalyticsMiddleware",
                        message: "Hub proxy invoke failed",
                        error: error,
                        data: {
                            socketHubUrl
                        }
                    }));
                }
            });
    };

    const start = () => {
        socketConnection.start()
            .then(() => {
                invoke('onConnectionInit', [{
                    token: player.signature,
                    session: sessionId,
                    pageload: sessionId,
                }]);
            })
            .catch(error => {
                store.dispatch(trackError({
                    logLevel: LogLevel.Warn,
                    category: "AnalyticsMiddleware",
                    message: "Connection error",
                    error: error,
                    data: {
                        socketHubUrl
                    }
                }));
            });
    };

    const track = (analyticsAction: IAnalyticsAction, state: IApplicationState) => {
        const trackingObject = {
            id: uuid(),
            sceneId: sceneId,
            userProgress: {
                coins: state.player.coins,
                lives: state.player.lives,
                score: state.player.score,
                levelId: state.game.levelInProgress,
            },
            ...analyticsAction
        };

        const eventName = [
            trackingObject.category,
            trackingObject.object,
            trackingObject.name,
            trackingObject.addition
        ]
        .join('-');

        if (allowDebug && logActions) {
            console.groupCollapsed(`[TRACK] ${eventName}`);
            console.table(flatten(trackingObject));
            console.groupEnd();
        }

        invoke('postActions', [[trackingObject]]);

        if (SDK.instance.supports.analytics) {
            try {
                SDK.instance.logEvent(eventName, trackingObject);
            }
            catch (error) {
                store.dispatch(trackError({
                    logLevel: LogLevel.Warn,
                    category: "AnalyticsMiddleware",
                    message: "sdk analytics logEvent failed",
                    error: error,
                    data: trackingObject
                }));
            }
        }
    };

    start();

    return action => {
        callOnIdle(() => {
            try {
                if (action.type === getType(loadScene.success)) {
                    sceneId = uuid();

                    if (allowDebug && logActions) {
                        console.groupCollapsed(`[SCENE] ${action.payload}`);
                        console.table(flatten({
                            id: sceneId,
                            name: action.payload
                        }));
                        console.groupEnd();
                    }
                }

                const state = store.getState() as IApplicationState;
                const handler = index[action.type];

                if (handler) {
                    const analyticsAction = handler(action, state);
                    if (analyticsAction) {
                        track(analyticsAction, state);
                    }
                }
            }
            catch (error) {
                store.dispatch(trackError({
                    category: "AnalyticsMiddleware",
                    message: "Track action failed",
                    error: error,
                    data: {
                        action: action.type
                    }
                }));
            }
        });

        return next(action);
    };
};